import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as Scrivito from "scrivito";
import { usePageType } from "hooks";

import cx from "classnames";
import {
  isLeftSidebarSubNavOpened,
  isProjectSummarySidebarVisible,
  isSystemConfiguratorLayout,
} from "utils";
import { Navigation } from "components/Navigation";
import { MobileLayoutPage } from "components/MobileLayout";
import { useDeviceType } from "./useDeviceType";

import { LeftSidebar } from "../LeftSidebar/LeftSidebar";
import NotFoundErrorPage from "../NotFoundErrorPage";

import { CurrentPageMetadata } from "../CurrentPageMetadata";
import { CookieConsentBanner } from "../CookieConsentBanner";
import { Footer } from "../Footer/Footer";
import { ProjectSummarySidebar } from "../ProjectSummarySidebar/ProjectSummarySidebar";
import { XCircleLoader } from "../XCircleLoader/XCircleLoader";
import { useProject } from "stores/project";

import s from "./Layout.module.scss";
import {
  useIsFetching,
  useIsLoaderXCircle,
  useIsModalOpen,
  useSetIsLoaderXCircle,
} from "stores/loadingStateStore";
import type { LeftSidebarAcitveButton } from "interfaces/shared.types";

const MemoizedCurrentPage = React.memo(() => <Scrivito.CurrentPage />);

export const Layout = Scrivito.connect(() => {
  const contentRef = useRef<HTMLInputElement>(null);
  const [activeButton, setActiveButton] =
    useState<LeftSidebarAcitveButton | null>(null);
  const pageType = usePageType();
  const device = useDeviceType();

  const isFetching = useIsFetching();
  const isLoaderXCircle = useIsLoaderXCircle();
  const isModalOpen = useIsModalOpen();
  const setIsLoaderXCircle = useSetIsLoaderXCircle();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setIsLoaderXCircle(true), []);

  const isSubNavOpened = useMemo(
    () => activeButton && isLeftSidebarSubNavOpened(activeButton),
    [activeButton],
  );

  const project = useProject();

  const isLeftSidebarVisible = pageType && isSystemConfiguratorLayout(pageType);
  const isRightSidebarVisible =
    pageType &&
    project &&
    isProjectSummarySidebarVisible(pageType, project?.id);

  const closeSubnav = useCallback(() => setActiveButton(null), []);
  const isOverlayActive =
    isSubNavOpened && activeButton && activeButton.subNavLevel?.value === 0;
  const { pathname } = window.location;

  useEffect(() => {
    if (device === "desktop") {
      contentRef?.current?.scrollTo(0, 0);
    }
  }, [pathname, device]);

  const shouldShowLoader = isLoaderXCircle && isFetching && !isModalOpen;

  if (device === "mobile") {
    return <MobileLayoutPage />;
  }

  return (
    <>
      <Navigation device={device} />
      <div className="d-flex h-100 flex-grow-1 overflow-hidden">
        {isLeftSidebarVisible && (
          <LeftSidebar
            activeButton={activeButton as LeftSidebarAcitveButton}
            setActiveButton={setActiveButton}
          />
        )}

        <div id="portal_left_sidebar" className={s.leftSidebarPortal} />
        {shouldShowLoader && <XCircleLoader />}
        <div
          className={cx(
            "content-wrapper d-flex flex-column position-relative overflow-hidden min-h-full page-start-top",
            pageType === "ProjectConfigurator" ? "bg-white" : "bg-gray-100",
            { "d-none": shouldShowLoader },
          )}
          ref={contentRef}
          id="contentWrapper"
        >
          <MemoizedCurrentPage />
          <NotFoundErrorPage />
          <CurrentPageMetadata />
          <CookieConsentBanner />
          <Footer />
          {isLeftSidebarVisible && (
            <div
              className={cx("position-fixed", s.leftSidebarOverlay, {
                [s.leftSidebarOverlayActive]: isOverlayActive,
              })}
              onClick={closeSubnav}
            />
          )}
        </div>
        {isRightSidebarVisible && (
          <ProjectSummarySidebar projectId={project?.id} />
        )}
      </div>
    </>
  );
});
